import clsx from 'clsx';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DP_FILTER_KEYS } from '../../utils/vacationDpStatus';
import type { VacationDpFilterKey } from '../../models/vacationDpBoard.types';
import {
    ACTIVE_FILTER_HINT,
    CARD_ACTIVE_CLASSES,
    CARD_FOCUS_CLASSES,
    CARD_ICON_WRAP_CLASSES,
    CARD_LABEL_CLASSES,
    CARD_TONE_CLASSES,
    CARD_VALUE_CLASSES,
    DP_FILTER_GROUPS,
    OVERLAP_HINT,
    VACATION_CARD_METADATA,
} from './vacationFilterSidebar.definitions';
import type { VacationFilterSidebarProps } from './vacationFilterSidebar.types';

const TABLE_MOBILE_MEDIA_QUERY = '(max-width: 1023px)';

interface FilterButtonProps {
    filterKey: VacationDpFilterKey;
    count: number;
    isActive: boolean;
    isCollapsed: boolean;
    onToggle: (key: VacationDpFilterKey) => void;
}

function FilterButton({ filterKey, count, isActive, isCollapsed, onToggle }: FilterButtonProps) {
    const { label, tone, description, icon: Icon } = VACATION_CARD_METADATA[filterKey];

    return (
        <button
            type="button"
            aria-pressed={isActive}
            aria-label={`${label}: ${count} ${description}`}
            title={isCollapsed ? label : undefined}
            onClick={() => onToggle(filterKey)}
            className={clsx(
                'flex items-center gap-3 rounded-lg border p-2.5 text-left transition-all',
                isCollapsed ? 'flex-col' : '',
                CARD_TONE_CLASSES[tone],
                CARD_FOCUS_CLASSES,
                isActive && CARD_ACTIVE_CLASSES[tone]
            )}
        >
            <span
                className={clsx(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                    CARD_ICON_WRAP_CLASSES[tone]
                )}
            >
                <Icon className="h-5 w-5" aria-hidden="true" />
            </span>
            {!isCollapsed && (
                <span className="min-w-0">
                    <span
                        className={clsx(
                            'block text-[11px] font-medium leading-tight font-montserrat',
                            CARD_LABEL_CLASSES[tone]
                        )}
                    >
                        {label}
                    </span>
                    <span
                        className={clsx(
                            'mt-0.5 block text-lg font-semibold font-montserrat',
                            CARD_VALUE_CLASSES[tone]
                        )}
                    >
                        {count}
                    </span>
                </span>
            )}
            {isCollapsed && (
                <span
                    className={clsx(
                        'text-lg font-semibold font-montserrat',
                        CARD_VALUE_CLASSES[tone]
                    )}
                >
                    {count}
                </span>
            )}
        </button>
    );
}

export function VacationFilterSidebar({
    counts,
    activeFilter,
    onToggleFilter,
    isLoading = false,
    isCollapsed = false,
    className,
    tourId,
}: VacationFilterSidebarProps) {
    const isMobile = useMediaQuery(TABLE_MOBILE_MEDIA_QUERY);
    const showCollapsed = !isMobile && isCollapsed;

    if (isLoading) {
        return (
            <div
                className={clsx(
                    'w-full lg:shrink-0 lg:sticky lg:top-4 lg:self-start transition-all duration-300',
                    showCollapsed ? 'lg:w-14' : 'lg:w-52',
                    className
                )}
                aria-hidden="true"
                data-tour={tourId}
            >
                <div className={clsx('flex flex-col gap-2', showCollapsed ? 'items-center' : '')}>
                    {Array.from({ length: 10 }).map((_, i) => (
                        <div
                            key={i}
                            className="animate-pulse rounded-lg border border-raro-gray-200 bg-white"
                            style={{
                                height: showCollapsed ? '48px' : '64px',
                                width: showCollapsed ? '40px' : '100%',
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div
            className={clsx(
                'w-full lg:shrink-0 lg:sticky lg:top-4 lg:self-start transition-all duration-300 flex flex-col gap-3',
                showCollapsed ? 'lg:w-14' : 'lg:w-52',
                className
            )}
            data-tour={tourId}
        >
            {/* Grupos de filtros */}
            {DP_FILTER_GROUPS.map((group) => (
                <div key={group.id} className="flex flex-col gap-2">
                    {/* Título da seção */}
                    {!showCollapsed && (
                        <h3 className="text-xs font-semibold font-montserrat uppercase tracking-widest text-raro-gray-700">
                            {group.label}
                        </h3>
                    )}
                    {showCollapsed && (
                        <hr className="border-t border-raro-gray-200" />
                    )}
                    {showCollapsed && (
                        <span className="sr-only">{group.label}</span>
                    )}

                    {/* Botões de filtro */}
                    <div
                        className={clsx(
                            showCollapsed ? 'flex flex-col gap-2 items-center' : 'flex flex-col gap-2'
                        )}
                    >
                        {group.keys.map((key: VacationDpFilterKey) => (
                            <FilterButton
                                key={key}
                                filterKey={key}
                                count={counts[key]}
                                isActive={activeFilter === key}
                                isCollapsed={showCollapsed}
                                onToggle={onToggleFilter}
                            />
                        ))}
                    </div>
                </div>
            ))}

            {/* Dicas - só aparecem quando não recolhido */}
            {!showCollapsed && (
                <div className="space-y-2 border-t border-raro-gray-100 pt-3">
                    <p className="text-[11px] text-raro-gray-500 font-montserrat">
                        {OVERLAP_HINT}
                    </p>
                    {activeFilter && (
                        <p className="text-[11px] text-raro-gray-500 font-montserrat">
                            {ACTIVE_FILTER_HINT}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export default VacationFilterSidebar;
